import type {
  BalanceBuyerResult,
  PayMethod,
} from '@b-stock/payments-methods-api-client'
import {
  PayMethodStatusEnum,
  PayMethodTypeEnum,
} from '@b-stock/payments-methods-api-client'

import type { PaymentRestrictions } from '@/buyer-portal/helpers/usePaymentRestrictions'

import type { CheckoutValues } from '../CheckoutValues'
import { isPaymentMethodDisabled } from '../PaymentMethods/utils'
import {
  PAY_WITH_TERMS,
  WIRE_TRANSFER,
  allowedSecondaryPaymentTypes,
  type NetTermsPayMethod,
} from '../shared'

export const calculateTotalPayAmount = (
  entries: CheckoutValues,
  onlySelected = false
) => {
  let total: number
  if (onlySelected) {
    const selectedMethod = entries.paymentMethods.find(
      (method) => method.id === entries.selected
    )
    total = selectedMethod?.amount ?? 0
  } else {
    total = entries.paymentMethods.reduce<number>(
      (sum, method) => sum + method.amount,
      0
    )
  }

  return total
}

const NO_RESTRICTIONS: PaymentRestrictions = {
  isACHBlocked: false,
  isCreditCardBlocked: false,
  isNetTermsBlocked: false,
}

export const isPayMethodBlocked = (
  method: PayMethod,
  restrictions: PaymentRestrictions
): boolean =>
  isPaymentMethodDisabled(method.type, {
    ach: restrictions.isACHBlocked,
    card: restrictions.isCreditCardBlocked,
  })

/**
 * Returns whether the given payment method type is a direct payment method —
 * one that belongs in the inline saved-methods list (CC, bank accounts), as
 * opposed to alternative payment options such as Net Terms or Wire Transfer.
 *
 * `allowCreditCards` is the visibility gate for card types. Callers are
 * responsible for deriving this from the appropriate combination of feature
 * flags and listing configuration. Per-listing and per-account restrictions
 * drive the `disableInput` state on rendered rows and are enforced separately
 * downstream.
 */
export const isDirectPaymentMethod = (
  type: PayMethodTypeEnum,
  allowCreditCards: boolean
): boolean =>
  (allowCreditCards &&
    (type === PayMethodTypeEnum.Card ||
      type === PayMethodTypeEnum.GetbalanceCc)) ||
  type === PayMethodTypeEnum.Bank ||
  type === PayMethodTypeEnum.StripeFinConn

export const getSortedPayEntities = (
  data?: PayMethod[],
  restrictions: PaymentRestrictions = NO_RESTRICTIONS
) => {
  if (!data) return []

  return data.slice().sort((a, b) => {
    const isFirstDisabled = isPayMethodBlocked(a, restrictions)
    const isSecondDisabled = isPayMethodBlocked(b, restrictions)

    // Disabled methods sort to the end
    if (isFirstDisabled !== isSecondDisabled) return isFirstDisabled ? 1 : -1

    // Within the same enabled/disabled group, sort by primaryAt descending
    if (!a.primaryAt) return 1 // primaryAt can be undefined
    if (!b.primaryAt) return -1
    return a.primaryAt < b.primaryAt ? 1 : -1
  })
}

export const getSortedValidPayEntities = (
  data?: PayMethod[],
  restrictions: PaymentRestrictions = NO_RESTRICTIONS,
  options?: {
    allowCreditCards?: boolean
  }
) => {
  // If no data provided, return empty array
  if (!data) {
    return []
  }

  const allowCreditCards = options?.allowCreditCards ?? false

  const validPayMethods = data.filter((method) => {
    if (method.status !== 'reusable') return false

    if (method.type === PayMethodTypeEnum.GetbalanceCc) {
      if (!allowCreditCards) return false

      const cardDetails = method.details as BalanceBuyerResult
      const creditCard = cardDetails.paymentData?.creditCards?.[0]
      return creditCard !== undefined
    }

    return (
      (method.type === PayMethodTypeEnum.Card && allowCreditCards) ||
      method.type === PayMethodTypeEnum.StripeFinConn ||
      method.type === PayMethodTypeEnum.Bank
    )
  })

  return getSortedPayEntities(validPayMethods, restrictions)
}

export const canPayWithNetTerms = (
  amountDue: number,
  balanceMethod?: NetTermsPayMethod
) =>
  // getbalance is typed as always-present, but a malformed payments-methods-svc
  // response can omit it at runtime.
  // oxlint-disable-next-line typescript-eslint/no-unnecessary-condition
  balanceMethod?.getbalance?.qualification?.status === 'approved' &&
  balanceMethod.details.creditInfo?.status !== 'suspended' &&
  balanceMethod.details.creditInfo?.availableCreditInCents != null &&
  balanceMethod.details.creditInfo.availableCreditInCents >= amountDue

// Net terms is denied when qualification is declined, Balance is suspended or the payment method record itself has failed.
export const isBuyerDenied = (method?: PayMethod | null): boolean => {
  if (method?.type !== PayMethodTypeEnum.Getbalance) return false
  const m = method as NetTermsPayMethod
  return (
    // oxlint-disable-next-line typescript-eslint/no-unnecessary-condition
    m.getbalance?.qualification?.status === 'declined' ||
    m.details.creditInfo?.status === 'suspended' ||
    m.status === PayMethodStatusEnum.Failed
  )
}

/**
 * Retrieves the secondary payment method from the selected payment methods array.
 * A secondary payment method is a card, bank, or stripe-fin-conn type method
 * that is used alongside net terms (getbalance).
 *
 * @param selectedPaymentMethods - Array of user-selected payment methods from formik context
 * @returns The secondary payment method if found, null otherwise
 */
export const getSecondaryPayMethod = (
  selectedPaymentMethods: Array<PayMethod & { amount: number }>
): (PayMethod & { amount: number }) | null => {
  return (
    selectedPaymentMethods.find(
      (method) =>
        method.type === PayMethodTypeEnum.Card ||
        method.type === PayMethodTypeEnum.Bank ||
        method.type === PayMethodTypeEnum.StripeFinConn ||
        method.type === PayMethodTypeEnum.GetbalanceCc
    ) ?? null
  )
}

/**
 * Determines if net terms is covered by a secondary payment method.
 * Checks if selectedPaymentMethods array contains both:
 * 1. A 'getbalance' type method (net terms)
 * 2. A secondary payment method (card, bank, or stripe-fin-conn)
 *
 * @param selectedPaymentMethods - Array of user-selected payment methods from formik context
 * @param isFeatureEnabled - Whether split payment feature is enabled
 * @returns true if net terms is covered by a secondary payment method
 */
export const isNetTermsCoveredBySecondary = (
  selectedPaymentMethods: Array<PayMethod & { amount: number }>,
  isFeatureEnabled: boolean
): boolean => {
  if (!isFeatureEnabled || selectedPaymentMethods.length < 2) {
    return false
  }

  const hasGetBalanceMethod = selectedPaymentMethods.some(
    (method) => method.type === 'getbalance'
  )

  const hasSecondaryMethod = selectedPaymentMethods.some(
    (method) =>
      method.type === PayMethodTypeEnum.Card ||
      method.type === PayMethodTypeEnum.Bank ||
      method.type === PayMethodTypeEnum.StripeFinConn ||
      method.type === PayMethodTypeEnum.GetbalanceCc
  )

  return hasGetBalanceMethod && hasSecondaryMethod
}

/**
 * Calculates the net terms amount to use for convenience fee calculation.
 * @param availableNetTermsCredit - Available net terms credit in cents
 * @param amountDue - Order amount due in cents (after applying B-Stock credits if applicable)
 * @returns The net terms amount to use for convenience fee calculation in cents
 */
export const calculateNetTermsAmountUsed = (
  availableNetTermsCredit: number,
  amountDue: number
): number => {
  return availableNetTermsCredit >= amountDue
    ? amountDue
    : availableNetTermsCredit
}

/**
 * Checks if the selected payment method is a credit card.
 * @param paymentMethods - Array of payment methods with amounts
 * @param selectedId - ID of the selected payment method
 * @returns true if the selected payment method is a credit card (Card or GetbalanceCc), false otherwise
 */
export const isCreditCardPaymentMethod = (
  paymentMethods: Array<PayMethod & { amount: number }>,
  selectedId: string
): boolean => {
  const selectedMethod = paymentMethods.find((m) => m.id === selectedId)
  if (selectedMethod === undefined) return false
  return (
    selectedMethod.type === PayMethodTypeEnum.Card ||
    selectedMethod.type === PayMethodTypeEnum.GetbalanceCc
  )
}

type ConvenienceFeeKind = 'creditCard' | 'netTerms' | null

/**
 * Classifies a single resolved payment method (or wire selection) for the
 * purpose of deciding which convenience-fee bucket applies in the bid /
 * Buy Now confirmation flow.
 *
 * Distinct from `isCreditCardPaymentMethod`:
 *   - Operates on a single `PayMethod` (already resolved by the caller)
 *     rather than a `(methods, id)` lookup.
 *   - Returns the fee kind, not just a boolean — Net Terms also bears a fee.
 *   - Treats `StripeFinConn` as fee-free (B-Stock uses Stripe only for ACH);
 *     `isCreditCardPaymentMethod`'s checkout caller handles split-pay
 *     differently and doesn't see this method as a CC either.
 *
 * Returns `null` for wire transfer, no method selected, or any method type
 * that does not bear a buyer-facing convenience fee (Bank, StripeFinConn,
 * credits, etc.).
 */
export const classifyConvenienceFeeFromMethod = (
  method: PayMethod | null | undefined,
  isWireSelected: boolean
): ConvenienceFeeKind => {
  if (isWireSelected) return null
  if (method === null || method === undefined) return null
  switch (method.type) {
    case PayMethodTypeEnum.Card:
    case PayMethodTypeEnum.GetbalanceCc:
      return 'creditCard'
    case PayMethodTypeEnum.Getbalance:
      return 'netTerms'
    default:
      return null
  }
}

/**
 * Gets the default payment method from available payment methods.
 * @param paymentMethods - Array of available payment methods
 * @returns The default payment method if found, null otherwise
 */
export const getDefaultPaymentMethod = (
  paymentMethods: PayMethod[],
  restrictions: PaymentRestrictions = NO_RESTRICTIONS
): PayMethod | null => {
  if (paymentMethods.length === 0) {
    return null
  }

  const sorted = getSortedPayEntities(paymentMethods, restrictions)
  return sorted.length > 0 ? (sorted[0] ?? null) : null
}

type PaymentMode =
  | 'ACH'
  | 'CREDIT_CARD'
  | 'WIRE_TRANSFER'
  | 'TERMS'
  | 'TERMS_SPLIT'
  | 'UNKNOWN'

const PAYMENT_TYPE_TO_MODE: Partial<Record<PayMethodTypeEnum, PaymentMode>> = {
  [PayMethodTypeEnum.Card]: 'CREDIT_CARD',
  [PayMethodTypeEnum.Bank]: 'ACH',
  [PayMethodTypeEnum.AchCreditTransfer]: 'ACH',
  [PayMethodTypeEnum.StripeFinConn]: 'ACH',
  [PayMethodTypeEnum.Wire]: 'WIRE_TRANSFER',
  [PayMethodTypeEnum.Getbalance]: 'TERMS',
  [PayMethodTypeEnum.GetbalanceCc]: 'CREDIT_CARD',
}

export const derivePaymentMethod = (values: CheckoutValues): PaymentMode => {
  if (values.selected === PAY_WITH_TERMS) {
    // Check if this is a split payment (Net Terms + Secondary payment method)
    const hasNetTermsPayment = values.selectedPaymentMethods.some(
      (method) => method.type === PayMethodTypeEnum.Getbalance
    )
    const hasSecondaryPayment = values.selectedPaymentMethods.some((method) =>
      allowedSecondaryPaymentTypes.includes(method.type)
    )

    if (hasNetTermsPayment && hasSecondaryPayment) {
      return 'TERMS_SPLIT'
    }
    return 'TERMS'
  }

  if (values.selected === WIRE_TRANSFER) return 'WIRE_TRANSFER'

  const type = values.paymentMethods.find((m) => m.id === values.selected)?.type

  return (type && PAYMENT_TYPE_TO_MODE[type]) ?? 'UNKNOWN'
}

/**
 * Simpler variant for the bid/buy-now flow where we already have
 * the resolved payment method type and wire selection state.
 * Returns undefined when no payment data is available (e.g. legacy flow).
 */
export const derivePaymentMode = (
  paymentMethodType?: PayMethodTypeEnum | null,
  isWireSelected?: boolean
): PaymentMode | undefined => {
  if (isWireSelected) return 'WIRE_TRANSFER'
  if (!paymentMethodType) return undefined
  return PAYMENT_TYPE_TO_MODE[paymentMethodType]
}
